import { useCallback, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Space } from 'antd';

import {
  CustomNotification,
  dialogFunction,
  Form,
  StyledModal,
  WithLoading,
} from 'common/components';
import SelfLoadedProductForm from 'common/components/product-add/SelfLoadedProductForm';

import { useDispatchReloadPage } from 'hooks/useReloadPage';

import useCreateProduct from 'hooks/selfLoadedProductHooks';
import {
  handleBarcodeAutoPopulation,
  useValidatePrimaryFieldsForActiveProduct,
} from 'hooks/useValidateSelfLoadedProduct';
import {
  useGetProductFullView,
  useGlobalModal,
  useGetProductOriginFullViewForProductOverlay,
  useGetMemberId,
} from 'hooks';
import { useValidateStatusForProductFull } from 'hooks/useProductStatusValidation';
import {
  IXONECertificationWrapper,
  TYPE_VIEW,
} from 'common/components/product-add/IXONECertificationWrapper';
import {
  CreateProductRequiredFieldsModal,
  dialogConfirmForCreateActiveProduct,
} from 'common/components/product-add/CreateProductRequiredFieldsModal';
import {
  isUnitPackageLevel,
  isValidCheckDigit,
} from 'common/components/product-add/components/utils/validation';
import { extractImageIds } from './components/utils/images';

import messages from 'i18n/messages/product';

import './CreateProductViaFormModal.less';
import { RequiredActiveProductRoot } from 'common/components/product-add/make-product-active/RequiredActiveProduct';
import { useQueryMemberPaymentSetting } from 'pages/company-profile/components/tabs/billing/queries';
import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

function CreateProductViaFormModal({
  isEdit,
  memberId: memberIdProp,
  productId,
  isAddEditProductModalOpen,
  initialGtin,
}) {
  const intl = useIntl();
  const reloadPage = useDispatchReloadPage();

  const [validateProducts, setValidateProducts] = useState([]);

  const [productData, setProductData] = useState();

  const [isRequiredFieldsModalVisible, setIsRequiredFieldsModalVisible] =
    useState(false);

  const callApiCallbackRef = useRef(() => {}); // mutable holder

  const { memberId: _memberId } = useGetMemberId();

  const memberId = memberIdProp ?? _memberId;

  const { data: memberPaymentSettingData } = useQueryMemberPaymentSetting({
    memberId,
    enabled: Boolean(isAddEditProductModalOpen) && Boolean(memberId),
  });

  const hasBypassProductInternalPermission = useCheckPermissions([
    {
      action: ABILITY_ACTION.BYPASS,
      subject: ABILITY_SUBJECT.PRODUCT_INTERNAL_MAINTENANCE,
    },
  ]);

  const onUpdateValidateProducts = useCallback((response) => {
    setValidateProducts([
      {
        activeResult: response?.data?.activeResult,
      },
    ]);
  }, []);

  const callApiCallback = useCallback(() => {
    callApiCallbackRef.current();
  }, []);

  const { closeModal, checkModalVisible, MODAL_NAMES } = useGlobalModal();
  const isAddEditModalOpen = checkModalVisible(
    MODAL_NAMES.ADD_EDIT_PRODUCT_MODAL
  );

  const handleCancel = () => {
    resetFormState();
    closeModal(MODAL_NAMES.ADD_EDIT_PRODUCT_MODAL);
  };

  const dialogCreateConfirm = dialogConfirmForCreateActiveProduct;

  const { productFull, invalidateAllProductFull } = useGetProductFullView({
    productId,
    enabled: !!productId,
  });

  const isPrivateLabel = productFull?.isPrivateLabel;

  const { data: originProductFull, invalidAllProductOriginFull } =
    useGetProductOriginFullViewForProductOverlay({
      productOverlayId: productId,
      enabled: Boolean(productId && productFull?.isOverlayProduct),
    });

  const {
    form,
    submitForm,
    formLoading,
    resetFormState,
    ...createProductFormProps
  } = useCreateProduct({
    setProductData,
    productData,
    modalVisible: isAddEditModalOpen,
    productDetail: productFull,
    isEdit,
    reloadPage,
    callApiCallback,
    memberId: memberId,
    productId,
    invalidateAllProductFull,
    invalidAllProductOriginFull,
    onUpdateValidateProducts,
    dialogConfirm: dialogCreateConfirm,
    onShowModalUpdate: () => setIsRequiredFieldsModalVisible(true),
    initialGtin,
  });

  const status = Form.useWatch('status', form);
  const isActiveStatus = status?.toLowerCase() === 'active';

  const packageLevel = Form.useWatch('packageLevel', form);
  const isUnit = isUnitPackageLevel(packageLevel);

  const {
    setValidateErrors,
    getFormPayload,
    productImagery,
    setProductImagery,
  } = createProductFormProps;

  const validatePrimaryFieldsMutation =
    useValidatePrimaryFieldsForActiveProduct({
      form,
      isActiveStatus,
      isUnit,
      setValidateErrors,
    });

  const { statusValidateProps, handleRevalidateActiveProduct } =
    useValidateStatusForProductFull({
      form,
      entityId: productId,
      isPrivateLabel,
      placement: 'rightBottom',
      getFormPayload,
      validatePrimaryFieldsMutation,
      setValidateProducts,
      validateProducts,
      onUpdateValidateProducts,
    });

  const isValidating = validatePrimaryFieldsMutation.isLoading;

  const modalProps = {
    title: isEdit
      ? intl.formatMessage(messages.editProduct)
      : intl.formatMessage(messages.addProduct),
    visible: isAddEditModalOpen,
    width: '85%',
    closable: true,
    maskClosable: false,
    onCancel: handleCancel,
  };

  /**
   * Validates product with either a custom payload or current form values.
   *
   * @param {Object} newPayload - Optional custom payload. If not provided, uses current form values.
   */
  const onValidateWithCurrentForm = async ({
    newPayload,
    isAdditional,
  } = {}) => {
    if (newPayload) {
      const payload = {
        ...newPayload,
        brickCode:
          newPayload?.brickCode ||
          (typeof newPayload?.brick === 'string'
            ? newPayload?.brick
            : newPayload?.brick?.brickCode || ''),
      };

      const response = await validatePrimaryFieldsMutation.mutateAsync(payload);
      if (response?.isSuccess) {
        onUpdateValidateProducts && onUpdateValidateProducts(response);
      }
    } else {
      if (isAdditional) {
        handleRevalidateActiveProduct();
      }
      await onValidateProduct();
    }
  };

  const onValidateProduct = async () => {
    const formValues = form.getFieldsValue(true);
    const brickCode = createProductFormProps?.brick;

    const payload = {
      ...formValues,
      brickCode:
        brickCode?.brickCode ||
        formValues?.brick?.brickCode ||
        formValues?.brickCode ||
        '',
      ...(productId && { productId, id: productId }),
    };

    const response = await validatePrimaryFieldsMutation.mutateAsync(payload);
    if (response?.isSuccess) {
      onUpdateValidateProducts && onUpdateValidateProducts(response);
    }
  };

  /**
   * Submits form data to IXONE confirmation.
   * Extracted to avoid code duplication in the onOk handler.
   */
  const submitToIXONE = (
    handleConfirmIXONE,
    formValue,
    brickCode,
    shouldClose
  ) => {
    callApiCallbackRef.current = shouldClose
      ? handleCancel
      : () => resetFormState();

    const productImageryIds = extractImageIds(productImagery);

    handleConfirmIXONE({
      originalData: productFull,
      changedFields: { ...formValue, ...brickCode, ...productImageryIds },
    });
  };

  /**
   * Handles comprehensive barcode auto-population during submit
   * Supports all barcode types: UPC-8, UPC-12, UPC-13, GTIN
   */
  const handleBarcodeAutoPopulationOnSubmit = async () => {
    // Get current form values
    const gtin = form.getFieldValue('gtin');
    const upc13 = form.getFieldValue('upc13');
    const upc12 = form.getFieldValue('upc12');
    const upc8 = form.getFieldValue('upc8');

    // Check if any barcode field is valid
    const isValidUpc8 = upc8 && isValidCheckDigit(upc8);
    const isValidUpc12 = upc12 && isValidCheckDigit(upc12);
    const isValidUpc13 = upc13 && isValidCheckDigit(upc13);
    const isValidGtin = gtin && isValidCheckDigit(gtin);

    // Auto-populate if any valid barcode exists
    if (isValidUpc8 || isValidUpc12 || isValidUpc13 || isValidGtin) {
      await handleBarcodeAutoPopulation(form, []);
      return true;
    }

    return false;
  };

  const handleSubmitProduct = async (
    handleConfirmIXONE,
    shouldClose = false
  ) => {
    const currentStatus = form.getFieldValue('status');
    const isActiveStatus = currentStatus?.toLowerCase() === 'active';
    const isUnit = isUnitPackageLevel(form.getFieldValue('packageLevel'));

    if (isActiveStatus && isUnit) {
      try {
        const brickCode = createProductFormProps?.brick;
        const formValue = await form.validateFields();

        // Success – submit directly
        submitToIXONE(handleConfirmIXONE, formValue, brickCode, shouldClose);
      } catch (error) {
        const errorFields = error?.errorFields ?? [];

        // Check if only barcode fields fail (GTIN, UPC-12, UPC-13, UPC-8)
        const barcodeFields = ['gtin', 'upc12', 'upc13', 'upc8'];
        const failedFields = errorFields.map((field) =>
          field?.name?.[0]?.toLowerCase()
        );
        const onlyBarcodeErrors = failedFields.every((field) =>
          barcodeFields.includes(field)
        );

        if (onlyBarcodeErrors && errorFields.length > 0) {
          // Try comprehensive barcode auto-population
          const wasAutoPopulated = await handleBarcodeAutoPopulationOnSubmit();

          if (wasAutoPopulated) {
            try {
              // Retry validation with auto-populated barcodes
              const brickCode = createProductFormProps?.brick;
              const formValue = await form.validateFields();

              submitToIXONE(
                handleConfirmIXONE,
                formValue,
                brickCode,
                shouldClose
              );
              return;
            } catch (retryError) {
              // If validation still fails, show error
              CustomNotification.error(
                'Please resolve all form errors before submitting'
              );
              return;
            }
          }
        }

        CustomNotification.error(
          'Please resolve all form errors before submitting'
        );
      }
    } else {
      // Non-active status: Only validate productName and packageLevel
      try {
        const brickCode = createProductFormProps?.brick;

        // Validate only required fields
        await form.validateFields(['productName', 'packageLevel']);

        // Get all form values (including non-validated fields)
        const formValue = form.getFieldsValue(true);

        // Success – submit with all values
        submitToIXONE(handleConfirmIXONE, formValue, brickCode, shouldClose);
      } catch (error) {
        CustomNotification.error(
          'Please resolve all form errors before submitting'
        );
      }
    }
  };

  return (
    <div>
      <IXONECertificationWrapper
        showModal={isEdit}
        type={TYPE_VIEW.EDIT_PRODUCT}
        onConfirm={(ixoneIgnore, fieldList) => {
          const currentStatus = form.getFieldValue('status');
          const isPrivateLabel = form.getFieldValue('isPrivateLabel');

          const isActiveStatus = currentStatus?.toLowerCase() === 'active';
          const hasActiveCharge = Boolean(
            memberPaymentSettingData?.productActiveRate
          );

          // Show charge warning only for active status with payment enabled
          if (
            isActiveStatus &&
            hasActiveCharge &&
            !isEdit &&
            !isPrivateLabel &&
            !hasBypassProductInternalPermission
          ) {
            dialogFunction({
              type: 'warn',
              content: 'There will be a charge for making this product active',
              okText: 'Submit',
              cancelText: 'Cancel',
              onOk: async () => {
                await submitForm(ixoneIgnore, fieldList);
              },
              width: 440,
            });
            return;
          }

          // Submit directly for all other cases
          submitForm(ixoneIgnore, fieldList);
        }}
        onCancel={handleCancel}
      >
        {(handleConfirmIXONE) => (
          <StyledModal
            {...modalProps}
            isLoading={formLoading}
            loadingContent={{ loadingText: 'Loading' }}
            destroyOnClose
            centered
            footer={
              <Space>
                <Button onClick={handleCancel} loading={isValidating}>
                  Cancel
                </Button>
                <Button onClick={onValidateProduct}>Validate Product</Button>
                {isEdit && (
                  <>
                    <Button
                      loading={formLoading}
                      onClick={async () => {
                        await handleSubmitProduct(handleConfirmIXONE, false);
                        handleRevalidateActiveProduct();
                      }}
                    >
                      Submit
                    </Button>
                  </>
                )}
                <Button
                  type='primary'
                  loading={formLoading}
                  onClick={() => handleSubmitProduct(handleConfirmIXONE, true)}
                >
                  {isEdit ? 'Submit & Close' : 'Submit'}
                </Button>
              </Space>
            }
          >
            <WithLoading loading={isValidating} tip='Validating...'>
              <SelfLoadedProductForm
                form={form}
                memberIdBrand={memberId}
                originProductFull={originProductFull}
                productFull={productFull}
                statusValidateProps={statusValidateProps}
                validateProducts={validateProducts}
                onRevalidateProduct={onValidateWithCurrentForm}
                {...createProductFormProps}
              />
            </WithLoading>
          </StyledModal>
        )}
      </IXONECertificationWrapper>

      {isRequiredFieldsModalVisible && (
        <RequiredActiveProductRoot>
          <CreateProductRequiredFieldsModal
            visible={isRequiredFieldsModalVisible}
            onCancel={() => setIsRequiredFieldsModalVisible(false)}
            newProductData={productData}
            productImagery={productImagery}
            validateProducts={validateProducts}
            setValidateProducts={setValidateProducts}
            setValidateErrors={setValidateErrors}
            reloadPage={reloadPage}
            setProductImagery={setProductImagery}
          />
        </RequiredActiveProductRoot>
      )}
    </div>
  );
}

export default CreateProductViaFormModal;
